import genericApi from './genericApi';

import Cart from '../models/Cart';
import GhaModel from '../models/Gha';
import TruckCart from '../models/TruckCart';
import ApiResponse from '../models/ApiResponse';
import CartCalculations from '../models/CartCalculation';
import CartServiceServerParams from '../models/CartServiceServerParams';
import DriverData from '../models/DriverData';
import GhaManualEntryDriverInfo from '../models/GhaManualEntryDriverInfo';
import { ReturnReason } from '../models/ReturnAwb';

export const buildCartServiceUrl = (url: string, isExport?: boolean, type?: string, reason?: string): string => {
  const params = new URLSearchParams();

  if (type === 'return') {
    params.append('type', 'return');
  } else if (isExport) {
    params.append('isExport', 'true');
  }

  if (reason) {
    params.append('reason', reason);
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
};

const cartRequestHandler = {

  getCart: (isGo?: boolean): Promise<ApiResponse<Cart>> => {
    let url = 'cart';
    if (isGo) {
      url += '?go=true';
    }
    return genericApi.get(url);
  },
  getTruckCart: (): Promise<ApiResponse<TruckCart>> => {

    return genericApi.get('cart?isExport=true');
  },
  getCartGha: (): Promise<ApiResponse<GhaModel>> => {

    return genericApi.get('cart/gha');
  },
  cartPay: (generatePO: boolean, paymentInfo: string, paymentType: string, stripePaymentMethodId?: string, orderNumber?: string, applyEwallet?: boolean, isExport?: boolean): Promise<ApiResponse<Cart>> => {
    let url = `cart/pay?generate_po=${generatePO}`;
    if(orderNumber) {
      url += `&poNumber=${orderNumber}`;
    }
    if(applyEwallet) {
      url += `&apply_ewallet=true`;
    }
    if(isExport) {
      url += `&isExport=true`;
    }
    return genericApi.post(url, { payment_info: paymentInfo, payment_type: paymentType, stripe_payment_method_id: stripePaymentMethodId });
  },
    cartGenGo: (poNumber?: string): Promise<ApiResponse<Cart>> => {
      let url = `cart/gen_go`;
      if (poNumber) {
        url += `?poNumber=${poNumber}`;
      }
      return genericApi.post(url);
    },
    prepEditGoOrder: (poNumber: string): Promise<ApiResponse<any>> => {
      return genericApi.get(`cart/prep_edit_go?poNumber=${poNumber}`);
    },
  getGoCharges: (poNumber: string, selectedAwbs?: Array<{code: string, number: string}>): Promise<ApiResponse<any>> => {
    let url = `pickup_order/${poNumber}/go_charges`;
    if (selectedAwbs && selectedAwbs.length > 0) {
      url += `?awbs=${encodeURIComponent(JSON.stringify(selectedAwbs))}`;
    }
    return genericApi.get(url);
  },
  bulkGetGoCharges: (awbs: Array<{ code: string, number: string, go_number: string }>): Promise<ApiResponse<any>> => {
    return genericApi.post(`bulk_go_charges`, { awbs });
  },
  convertGoToPo: (poNumber: string): Promise<ApiResponse<any>> => {
    return genericApi.post(`pickup_order/${poNumber}/convert_go`);
  },
  convertGoToPoPayment: (poNumber: string, paymentType: string, paymentInfo: string, stripePaymentMethodId?: string): Promise<ApiResponse<any>> => {
    return genericApi.post(`pickup_order/${poNumber}/convert_go`, {
      payment_type: paymentType,
      payment_info: paymentInfo,
      stripe_payment_method_id: stripePaymentMethodId
    });
  },
  addAirwayBillToCart: (awbCode: string, awbNumber: string, version: number, editOrder?: string, skipStatusCheck?: boolean): Promise<ApiResponse<Cart>> => {
    let url = `cart/list/${awbCode}/${awbNumber}/${version}`
    if (editOrder) {
      url += `?edit=${editOrder}`;
      if (skipStatusCheck) url += `&skipStatusCheck=true`;
    } else if (skipStatusCheck) {
      url += `?skipStatusCheck=true`;
    }
    return genericApi.put(url);
  },
  addAirwayBillToReturnCart: (awbCode: string, awbNumber: string, version: number): Promise<ApiResponse<Cart>> => {
    let url = `cart/return/${awbCode}/${awbNumber}/${version}`;
    return genericApi.put(url);
  },
  addExportAirwayBillToCart: (awbCode: string, awbNumber: string, ver: number, skipStatusCheck?: boolean): Promise<ApiResponse<TruckCart>> => {

    return genericApi.put(`cart/list/${awbCode}/${awbNumber}/${ver}?isExport=true${skipStatusCheck?"&skipStatusCheck=true":""}`);
  },
  addAirwayBillPartToCart: (
    awbCode: string,
    awbNumber: string,
    versionOrPartId: number | string,
    partId?: string,
    skipStatusCheck?: boolean
  ): Promise<ApiResponse<Cart>> => {
    if (typeof versionOrPartId === 'number' && partId) {
      let url = `cart/list/${awbCode}/${awbNumber}/${versionOrPartId}/${partId}`;
      if (skipStatusCheck) {
        url += `?skipStatusCheck=true`;
      }
      return genericApi.put(url);
    }

    return genericApi.put(`cart/list/${awbCode}/${awbNumber}/${versionOrPartId}`);
  },
  addExportAirwayBillPartToCart: (
    awbCode: string,
    awbNumber: string,
    versionOrPartId: number | string,
    partId?: string,
    skipStatusCheck?: boolean
  ): Promise<ApiResponse<TruckCart>> => {
    if (typeof versionOrPartId === 'number' && partId) {
      return genericApi.put(`cart/list/${awbCode}/${awbNumber}/${versionOrPartId}/${partId}?isExport=true${skipStatusCheck?"&skipStatusCheck=true":""}`);
    }

    return genericApi.put(`cart/list/${awbCode}/${awbNumber}/${versionOrPartId}?isExport=true${skipStatusCheck?"&skipStatusCheck=true":""}`);
  },
  deleteAwbFromCart: (awbCode: string, awbNumber: string): Promise<ApiResponse<Cart>> => {

    return genericApi.delete(`cart/list/${awbCode}/${awbNumber}`);
  },
  deleteExportAwbFromCart: (awbCode: string, awbNumber: string): Promise<ApiResponse<TruckCart>> => {

    return genericApi.delete(`cart/list/${awbCode}/${awbNumber}?isExport=true`);
  },
  addServiceToCart: (awbCode: string, awbNumber: string, version: number, params: Array<CartServiceServerParams>, isExport?: boolean, type?: string): Promise<ApiResponse<Cart>> => {
    const url = buildCartServiceUrl(`cart/service/${awbCode}/${awbNumber}/${version}`, isExport, type);
    return genericApi.put(url, params);
  },
  updateServiceToCart: (awbCode: string, awbNumber: string, params: CartServiceServerParams, isExport?: boolean, type?: string): Promise<ApiResponse<Cart>> => {
    const url = buildCartServiceUrl(`cart/service/${awbCode}/${awbNumber}`, isExport, type);
    return genericApi.post(url, params);
  },
  deleteServiceFromCart: (awbCode: string, awbNumber: string, service: string, reason?: string, isExport?: boolean, type?: string): Promise<ApiResponse<Cart>> => {
    let url: string = `cart/service/${awbCode}/${awbNumber}/${service}`;

    if (service === 'other' || service === 'gha_adjust') {
      url = buildCartServiceUrl(url, isExport, type, reason);
    } else {
      url = buildCartServiceUrl(url, isExport, type);
    }

    return genericApi.delete(url);
  },
  changeCartPickupDate: (date: string): Promise<ApiResponse<Cart>> => {

    return genericApi.post(`cart/pickupdate/${date}`);
  },
  changeTruckCartPickupDate: (date: string): Promise<ApiResponse<TruckCart>> => {

    return genericApi.post(`cart/pickupdate/${date}?isExport=true`);
  },
  getCartPickupDate: (): Promise<ApiResponse<string>> => {

    return genericApi.get('cart/pickupdate');
  },
  generateDropOffOrder: (): Promise<ApiResponse<TruckCart>> => {

    return genericApi.post('cart/gen_drop_off');
  },
  returnPay: (generatePO: boolean, driverInfo: GhaManualEntryDriverInfo, returnInfo: ReturnReason, awb: any, paymentType: string): Promise<ApiResponse<any>> => {
    if (driverInfo.arrivalTime.length === 5) {
      driverInfo.arrivalTime = driverInfo.arrivalTime.substr(0, 2) + driverInfo.arrivalTime.substr(3, 2);
    }
    return genericApi.post(`cart/return?generate_po=${generatePO}`, { driver_info: driverInfo, return_info: returnInfo, awb, payment_info: paymentType });
  },
  getCartPay: (applyEWallet?: boolean, paymentType?: string): Promise<ApiResponse<CartCalculations>> => {
    let url = 'cart/pay';
    if (applyEWallet) {
      url += '?apply_ewallet=true';
    }
    if (paymentType && applyEWallet) {
      url += `&payment_type=${paymentType}`;
    }
    return genericApi.get(url);
  },
  emptyCart: (): Promise<ApiResponse<void>> => {
    return genericApi.delete('cart');
  }
};

export default cartRequestHandler;